import { ElementStyles, FASTElement, Updates } from "@microsoft/fast-element";

// TODO: Open this up to allow for registering other types of targets.

/**
 * A target that can have key/value pairs set and removed.
 * @public
 */
export interface PropertyTarget {
    setProperty(name: string, value: string): void;
    removeProperty(name: string): void;
}

abstract class QueuedStyleSheetTarget {
    protected abstract target: PropertyTarget;

    public setProperty(name: string, value: string) {
        Updates.enqueue(() => this.target.setProperty(name, value));
    }

    public removeProperty(name: string) {
        Updates.enqueue(() => this.target.removeProperty(name));
    }
}

/**
 * Handles setting properties for a FASTElement using Constructable Stylesheets
 */
class FASTStyleSheetTarget extends QueuedStyleSheetTarget {
    protected target: PropertyTarget;

    constructor(source: FASTElement) {
        super();

        const sheet = new CSSStyleSheet();
        this.target = (sheet.cssRules[sheet.insertRule(":host{}")] as CSSStyleRule).style;
        source.$fastController.addStyles(new ElementStyles([sheet]));
    }
}

/**
 * Handles setting properties for a document using Constructable Stylesheets
 */
class DocumentStyleSheetTarget extends QueuedStyleSheetTarget {
    protected target: PropertyTarget;
    constructor() {
        super();

        const sheet = new CSSStyleSheet();
        this.target = (sheet.cssRules[sheet.insertRule(":root{}")] as CSSStyleRule).style;

        (document as any).adoptedStyleSheets = [
            ...(document as any).adoptedStyleSheets,
            sheet,
        ];
    }
}

/**
 * Controls emission for default values. This control is capable
 * of emitting to multiple {@link PropertyTarget | PropertyTargets},
 * and only emits if it has at least one root.
 *
 * @internal
 */
export class RootStyleSheetTarget {
    private static roots = new Set<PropertyTarget>();
    private static properties: Record<string, string> = {};

    public setProperty(name: string, value: any): void {
        RootStyleSheetTarget.properties[name] = value;

        for (const target of RootStyleSheetTarget.roots.values()) {
            target.setProperty(name, value);
        }
    }

    public removeProperty(name: string): void {
        delete RootStyleSheetTarget.properties[name];
        for (const target of RootStyleSheetTarget.roots.values()) {
            target.removeProperty(name);
        }
    }

    public static registerRoot(root: PropertyTarget) {
        const { roots } = RootStyleSheetTarget;
        if (!roots.has(root)) {
            roots.add(root);
            for (const key in RootStyleSheetTarget.properties) {
                root.setProperty(key, RootStyleSheetTarget.properties[key]);
            }
        }
    }

    public static unregisterRoot(root: PropertyTarget) {
        const { roots } = RootStyleSheetTarget;
        if (roots.has(root)) {
            roots.delete(root);

            for (const key in RootStyleSheetTarget.properties) {
                root.removeProperty(key);
            }
        }
    }
}

const propertyTargetCache: WeakMap<FASTElement | Document, PropertyTarget> = new WeakMap();

/**
 * Manages creation and caching of QueuedStyleSheetTarget instances.
 */
export const PropertyTargetManager = Object.freeze({
    getOrCreate(source: FASTElement | Document): PropertyTarget {
        if (propertyTargetCache.has(source))
            return propertyTargetCache.get(source)!;

        let target: QueuedStyleSheetTarget;

        target = source instanceof Document
            ? new DocumentStyleSheetTarget()
            : new FASTStyleSheetTarget(source);

            propertyTargetCache.set(source, target);

        return target;
    },
});
